'use client';

import { useMemo, useState } from 'react';
import { Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { Availability } from '@/types/tournament.model';
import { AvailabilityDayCard } from './AvailabilityDayCard';

interface AvailabilitySectionProps {
  availability: Availability[];
  schedule?: Availability[];
  className?: string;
  /** If present, shows an "Editar" button, even when the team declared nothing yet. */
  onEdit?: () => void;
}

export function AvailabilitySection({
  availability,
  schedule,
  className,
  onEdit,
}: AvailabilitySectionProps) {
  const [isCompact, setIsCompact] = useState(true);

  const sortedAvailability = useMemo(() => {
    return [...availability].sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );
  }, [availability]);

  const isEmpty = availability.length === 0;
  if (isEmpty && !onEdit) return null;

  return (
    <div className={className}>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h4 className="font-semibold text-gray-900">Disponibilidad</h4>
        <div className="flex flex-wrap items-center gap-2">
          {onEdit && (
            <Button type="button" variant="outline" size="sm" onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" />
              Editar
            </Button>
          )}
          {!isEmpty && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-transparent"
              onClick={() => setIsCompact((prev) => !prev)}
            >
              {isCompact
                ? 'Expandir disponibilidades'
                : 'Colapsar disponibilidades'}
            </Button>
          )}
        </div>
      </div>

      {isEmpty && (
        <p className="text-sm text-gray-500">
          La pareja no cargó disponibilidad.
        </p>
      )}

      <div className="space-y-3">
        {sortedAvailability.map((day) => (
          <AvailabilityDayCard
            key={`${day.date}-${day.dayOfWeek}`}
            date={day.date}
            dayOfWeek={day.dayOfWeek}
            slots={day.timeRange ?? []}
            scheduleSlots={
              schedule?.find((s) => s.date === day.date)?.timeRange
            }
            isCompact={isCompact}
          />
        ))}
      </div>
    </div>
  );
}
